import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import styles from './GearPage.module.css';
import API_BASE_URL from '../config/api';
import GearPageSkeleton from '../components/GearPageSkeleton';
import WeightCalculatorPopup from '../components/WeightCalculatorPopup';
import BetaGearPopup from '../components/BetaGearPopup';
import GearCategorySection from '../components/GearCategorySection';
import GearCategoryView from '../components/GearCategoryView';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { getPriceSettings, PRICE_SETTINGS_STORAGE_KEY } from '../utils/priceSettings';
import { useUser } from '../context/UserContext';

const SALES_EQUIPMENT_CATEGORIES = [
  'Backpacks',
  'Trekking poles',
  'Head lamps',
  'Location',
  'Ice axes',
  'Crampons',
  'Helmets',
  'Harnesses',
  'Hardware',
  'Crevasse rescue',
  'Ropes',
  'Climbing other',
  'Eyewear'
];

const CLOTHING_CATEGORIES = new Set([
  'Hard shell · Jackets',
  'Hard shell · Pants',
  'Hardshell Jackets',
  'Hardshell Pants',
  'Soft shell · Jackets',
  'Soft shell · Pants',
  'Softshell Jackets',
  'Softshell Pants',
  'Fleece',
  'Down',
  'Midlayer · Fleece',
  'Midlayer · Down',
  'Midlayer · Other',
  'Insulation · Waterproof',
  'Insulation · Synthetic',
  'Insulation · Down',
  'Base layer · Merino',
  'Base layer · Blends',
  'Base layer · Synthetic',
  'Base layer · Mesh',
  'Base layers · Merino',
  'Base layers · Blends',
  'Base layers · Synthetic',
  'Base layers · Mesh'
]);

const FOOTWEAR_CATEGORIES = new Set([
  'Boots',
  'Hiking Boots',
  'Hiking Shoes',
  'Trail running'
]);

const SHOW_GEAR_PAGE_PLACEHOLDER = false;

const MENU = [
  {
    id: 'watchlist',
    label: 'Watchlist',
    categories: [
      {
        id: 'watchlist-items',
        label: 'Your Watchlist',
        subcategories: [
          {
            id: 'watchlist-items-all',
            label: 'Watchlist',
            items: [
              { id: 'watchlist-items-all-entries', label: 'Watchlist', filters: { watchlistOnly: true, sort: 'price-asc', limit: 10 } }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'men',
    label: 'Men',
    categories: [
      {
        id: 'men-sales',
        label: 'Sales of the day',
        subcategories: [
          {
            id: 'men-sales-all',
            label: 'Sales',
            items: [
              { id: 'men-sales-all-items-men', label: 'Men', dataCategories: [...CLOTHING_CATEGORIES, ...FOOTWEAR_CATEGORIES], filters: { saleOnly: true, minDiscountPercent: 9, sort: 'price-asc', limit: 40, genderIncludes: ['men', 'male', 'man', 'unisex'] } }
            ]
          }
        ]
      },
      {
        id: 'men-footwear',
        label: 'Footwear',
        subcategories: [
          {
            id: 'men-footwear-mountaineering',
            label: 'Mountaineering',
            items: [
              { id: 'men-footwear-mountaineering-b1', label: 'B1', dataCategories: ['Boots'], viewId: 'boots', filters: { cramponCompat: ['B1'] } },
              { id: 'men-footwear-mountaineering-b2', label: 'B2', dataCategories: ['Boots'], viewId: 'boots', filters: { cramponCompat: ['B2'] } },
              { id: 'men-footwear-mountaineering-b3', label: 'B3', dataCategories: ['Boots'], viewId: 'boots', filters: { cramponCompat: ['B3'] } }
            ]
          },
          {
            id: 'men-footwear-hiking',
            label: 'Hiking',
            items: [
              { id: 'men-footwear-hiking-boots', label: 'Boots', dataCategories: ['Hiking Boots'], viewId: 'boots' },
              { id: 'men-footwear-hiking-shoes', label: 'Shoes', dataCategories: ['Hiking Shoes'] }
            ]
          },
          {
            id: 'men-footwear-trail',
            label: 'Trail running',
            items: [
              { id: 'men-footwear-trail-running', label: 'Trail running', dataCategories: ['Trail running'] }
            ]
          },
        ]
      },
      {
        id: 'men-clothing',
        label: 'Clothing',
        subcategories: [
          {
            id: 'men-clothing-hardshell',
            label: 'Hard shell',
            items: [
              { id: 'men-clothing-hardshell-jackets', label: 'Jackets', dataCategories: ['Hard shell · Jackets', 'Hardshell Jackets'] },
              { id: 'men-clothing-hardshell-pants', label: 'Pants', dataCategories: ['Hard shell · Pants', 'Hardshell Pants'] }
            ]
          },
          {
            id: 'men-clothing-softshell',
            label: 'Soft shell',
            items: [
              { id: 'men-clothing-softshell-jackets', label: 'Jackets', dataCategories: ['Soft shell · Jackets', 'Softshell Jackets'] },
              { id: 'men-clothing-softshell-pants', label: 'Pants', dataCategories: ['Soft shell · Pants', 'Softshell Pants'] }
            ]
          },
          {
            id: 'men-clothing-midlayer',
            label: 'Midlayer',
            items: [
              { id: 'men-clothing-midlayer-fleece', label: 'Fleece', dataCategories: ['Midlayer · Fleece', 'Fleece'] },
              { id: 'men-clothing-midlayer-down', label: 'Down', dataCategories: ['Midlayer · Down', 'Down'] },
              { id: 'men-clothing-midlayer-other', label: 'Other', dataCategories: ['Midlayer · Other'] }
            ]
          },
          {
            id: 'men-clothing-insulation',
            label: 'Insulation',
            comingSoon: true,
            items: [
              { id: 'men-clothing-insulation-waterproof', label: 'Waterproof', dataCategories: ['Insulation · Waterproof'] },
              { id: 'men-clothing-insulation-synthetic', label: 'Synthetic', dataCategories: ['Insulation · Synthetic'] },
              { id: 'men-clothing-insulation-down', label: 'Down', dataCategories: ['Insulation · Down'] }
            ]
          },
          {
            id: 'men-clothing-baselayer',
            label: 'Base layer',
            items: [
              { id: 'men-clothing-baselayer-merino', label: 'Merino', dataCategories: ['Base layer · Merino', 'Base layers · Merino'] },
              { id: 'men-clothing-baselayer-blends', label: 'Blends', dataCategories: ['Base layer · Blends', 'Base layers · Blends'] },
              { id: 'men-clothing-baselayer-synthetic', label: 'Synthetic', dataCategories: ['Base layer · Synthetic', 'Base layers · Synthetic'] },
              { id: 'men-clothing-baselayer-mesh', label: 'Mesh', dataCategories: ['Base layer · Mesh', 'Base layers · Mesh'] }
            ]
          }
        ]
      },
      {
        id: 'men-accessories',
        label: 'Accessories',
        comingSoon: true,
        subcategories: [
          {
            id: 'men-accessories-all',
            label: 'Accessories',
            items: [
              { id: 'men-accessories-all-items', label: 'All accessories', dataCategories: ['Accessories', 'Gloves', 'Beanies', 'Hiking Gloves', 'Hiking Beanies'] }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'women',
    label: 'Women',
    categories: [
      {
        id: 'women-sales',
        label: 'Sales of the day',
        subcategories: [
          {
            id: 'women-sales-all',
            label: 'Sales',
            items: [
              { id: 'women-sales-all-items-women', label: 'Women', dataCategories: [...CLOTHING_CATEGORIES, ...FOOTWEAR_CATEGORIES], filters: { saleOnly: true, minDiscountPercent: 9, sort: 'price-asc', limit: 40, genderIncludes: ['women', 'female', 'woman', 'unisex'] } }
            ]
          }
        ]
      },
      {
        id: 'women-footwear',
        label: 'Footwear',
        subcategories: [
          {
            id: 'women-footwear-mountaineering',
            label: 'Mountaineering',
            items: [
              { id: 'women-footwear-mountaineering-b1', label: 'B1', dataCategories: ['Boots'], viewId: 'boots', filters: { cramponCompat: ['B1'] } },
              { id: 'women-footwear-mountaineering-b2', label: 'B2', dataCategories: ['Boots'], viewId: 'boots', filters: { cramponCompat: ['B2'] } },
              { id: 'women-footwear-mountaineering-b3', label: 'B3', dataCategories: ['Boots'], viewId: 'boots', filters: { cramponCompat: ['B3'] } }
            ]
          },
          {
            id: 'women-footwear-hiking',
            label: 'Hiking',
            items: [
              { id: 'women-footwear-hiking-boots', label: 'Boots', dataCategories: ['Hiking Boots'], viewId: 'boots' },
              { id: 'women-footwear-hiking-shoes', label: 'Shoes', dataCategories: ['Hiking Shoes'] }
            ]
          },
          {
            id: 'women-footwear-trail',
            label: 'Trail running',
            items: [
              { id: 'women-footwear-trail-running', label: 'Trail running', dataCategories: ['Trail running'] }
            ]
          },
        ]
      },
      {
        id: 'women-clothing',
        label: 'Clothing',
        subcategories: [
          {
            id: 'women-clothing-hardshell',
            label: 'Hard shell',
            items: [
              { id: 'women-clothing-hardshell-jackets', label: 'Jackets', dataCategories: ['Hard shell · Jackets', 'Hardshell Jackets'] },
              { id: 'women-clothing-hardshell-pants', label: 'Pants', dataCategories: ['Hard shell · Pants', 'Hardshell Pants'] }
            ]
          },
          {
            id: 'women-clothing-softshell',
            label: 'Soft shell',
            items: [
              { id: 'women-clothing-softshell-jackets', label: 'Jackets', dataCategories: ['Soft shell · Jackets', 'Softshell Jackets'] },
              { id: 'women-clothing-softshell-pants', label: 'Pants', dataCategories: ['Soft shell · Pants', 'Softshell Pants'] }
            ]
          },
          {
            id: 'women-clothing-midlayer',
            label: 'Midlayer',
            items: [
              { id: 'women-clothing-midlayer-fleece', label: 'Fleece', dataCategories: ['Midlayer · Fleece', 'Fleece'] },
              { id: 'women-clothing-midlayer-down', label: 'Down', dataCategories: ['Midlayer · Down', 'Down'] },
              { id: 'women-clothing-midlayer-other', label: 'Other', dataCategories: ['Midlayer · Other'] }
            ]
          },
          {
            id: 'women-clothing-insulation',
            label: 'Insulation',
            comingSoon: true,
            items: [
              { id: 'women-clothing-insulation-waterproof', label: 'Waterproof', dataCategories: ['Insulation · Waterproof'] },
              { id: 'women-clothing-insulation-synthetic', label: 'Synthetic', dataCategories: ['Insulation · Synthetic'] },
              { id: 'women-clothing-insulation-down', label: 'Down', dataCategories: ['Insulation · Down'] }
            ]
          },
          {
            id: 'women-clothing-baselayer',
            label: 'Base layer',
            items: [
              { id: 'women-clothing-baselayer-merino', label: 'Merino', dataCategories: ['Base layer · Merino', 'Base layers · Merino'] },
              { id: 'women-clothing-baselayer-blends', label: 'Blends', dataCategories: ['Base layer · Blends', 'Base layers · Blends'] },
              { id: 'women-clothing-baselayer-synthetic', label: 'Synthetic', dataCategories: ['Base layer · Synthetic', 'Base layers · Synthetic'] },
              { id: 'women-clothing-baselayer-mesh', label: 'Mesh', dataCategories: ['Base layer · Mesh', 'Base layers · Mesh'] }
            ]
          }
        ]
      },
      {
        id: 'women-accessories',
        label: 'Accessories',
        comingSoon: true,
        subcategories: [
          {
            id: 'women-accessories-all',
            label: 'Accessories',
            items: [
              { id: 'women-accessories-all-items', label: 'All accessories', dataCategories: ['Accessories', 'Gloves', 'Beanies', 'Hiking Gloves', 'Hiking Beanies'] }
            ]
          }
        ]
      }
    ]
  },
  {
    id: 'equipment',
    label: 'Equipment',
    categories: [
      {
        id: 'equipment-sales',
        label: 'Sales of the day',
        subcategories: [
          {
            id: 'equipment-sales-all',
            label: 'Sales',
            items: [
              { id: 'equipment-sales-all-items-equipment', label: 'Equipment', dataCategories: SALES_EQUIPMENT_CATEGORIES, filters: { saleOnly: true, minDiscountPercent: 9, sort: 'price-asc', limit: 40 } }
            ]
          }
        ]
      },
      {
        id: 'equipment-backpacks',
        label: 'Backpacks',
        subcategories: [
          {
            id: 'equipment-backpacks-all',
            label: 'Backpacks',
            items: [
              { id: 'equipment-backpacks-small', label: 'Small (<25L)', dataCategories: ['Backpacks'], filters: { litersMax: 24.9 } },
              { id: 'equipment-backpacks-medium', label: 'Medium (25-40L)', dataCategories: ['Backpacks'], filters: { litersMin: 25, litersMax: 40 } },
              { id: 'equipment-backpacks-large', label: 'Large (40L+)', dataCategories: ['Backpacks'], filters: { litersMin: 40.01 } },
              { id: 'equipment-backpacks-unspecified', label: 'Unspecified', dataCategories: ['Backpacks'], filters: { litersMissing: true } }
            ]
          }
        ]
      },
      {
        id: 'equipment-trekking-poles',
        label: 'Trekking poles',
        subcategories: [
          {
            id: 'equipment-trekking-poles-all',
            label: 'Trekking poles',
            items: [
              { id: 'equipment-trekking-poles-items', label: 'All trekking poles', dataCategories: ['Trekking poles'] }
            ]
          }
        ]
      },
      {
        id: 'equipment-electronics',
        label: 'Electronics',
        subcategories: [
          {
            id: 'equipment-electronics-headlamps',
            label: 'Head lamps',
            items: [
              { id: 'equipment-electronics-headlamps-items', label: 'All head lamps', dataCategories: ['Head lamps'] }
            ]
          },
          {
            id: 'equipment-electronics-location',
            label: 'Location',
            comingSoon: true,
            items: [
              { id: 'equipment-electronics-location-items', label: 'All location gear', dataCategories: ['Location'] }
            ]
          }
        ]
      },
      {
        id: 'equipment-alpinism',
        label: 'Alpinism',
        subcategories: [
          {
            id: 'equipment-alpinism-ice-axes',
            label: 'Ice axes',
            items: [
              { id: 'equipment-alpinism-ice-axes-items', label: 'All ice axes', dataCategories: ['Ice axes'], viewId: 'crampons_axes' }
            ]
          },
          {
            id: 'equipment-alpinism-crampons',
            label: 'Crampons',
            items: [
              { id: 'equipment-alpinism-crampons-strap', label: 'Strap-on (C1)', dataCategories: ['Crampons'], viewId: 'crampons_axes', filters: { cramponCat: ['Strap-on (C1)'] } },
              { id: 'equipment-alpinism-crampons-semi', label: 'Semi-automatic (C2)', dataCategories: ['Crampons'], viewId: 'crampons_axes', filters: { cramponCat: ['Semi-automatic (C2)'] } },
              { id: 'equipment-alpinism-crampons-auto', label: 'Automatic (C3)', dataCategories: ['Crampons'], viewId: 'crampons_axes', filters: { cramponCat: ['Automatic (C3)'] } },
              { id: 'equipment-alpinism-crampons-hybrid', label: 'Hybrid', dataCategories: ['Crampons'], viewId: 'crampons_axes', filters: { isHybridCrampon: true } }
            ]
          },
          {
            id: 'equipment-alpinism-climbing',
            label: 'Climbing',
            items: [
              { id: 'equipment-alpinism-climbing-helmets', label: 'Helmets', dataCategories: ['Helmets'] },
              { id: 'equipment-alpinism-climbing-harnesses', label: 'Harnesses', dataCategories: ['Harnesses'] },
              { id: 'equipment-alpinism-climbing-hardware', label: 'Hardware', dataCategories: ['Hardware'] },
              { id: 'equipment-alpinism-crevasse', label: 'Crevasse rescue', dataCategories: ['Crevasse rescue'] },
              { id: 'equipment-alpinism-ropes', label: 'Ropes', dataCategories: ['Ropes'] },
              { id: 'equipment-alpinism-climbing-other', label: 'Other', dataCategories: ['Climbing other'] }
            ]
          },
          {
            id: 'equipment-alpinism-eyewear',
            label: 'Eyewear',
            items: [
              { id: 'equipment-alpinism-eyewear-items', label: 'All eyewear', dataCategories: ['Eyewear'] }
            ]
          }
        ]
      },
    ]
  }
];

function GearPage({ onOpenLogin }) {
  const { currentUser } = useUser();
  const initializedDefaultSectionRef = useRef(false);
  const getStoredSelection = (key) => {
    try {
      return localStorage.getItem(key) || null;
    } catch {
      return null;
    }
  };

  const [gear, setGear] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCalculator, setShowCalculator] = useState(false);
  const [showBetaPopup, setShowBetaPopup] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const [activeSectionId, setActiveSectionId] = useState(() => {
    const saved = getStoredSelection('activeGearSection');
    // If there is no saved section, or the saved section is watchlist but they aren't logged in (assumed by default if loading),
    // default to 'men' (Sales of the Day). Wait, they could be logged in but loading. 
    // We already handle that by showing "Please log in" placeholder.
    const defaultSection = 'men';
    return saved && MENU.find((s) => s.id === saved) ? saved : defaultSection;
  });

  const [activeCategoryId, setActiveCategoryId] = useState(() => getStoredSelection('activeGearCategory'));
  const [activeSubcategoryId, setActiveSubcategoryId] = useState(() => getStoredSelection('activeGearSubcategory'));
  const [activeItemId, setActiveItemId] = useState(() => getStoredSelection('activeGearItem'));

  const [menuSelection, setMenuSelection] = useState({
    section: null,
    category: null,
    subCategory: null
  });
  const [priceSettings, setPriceSettings] = useState(() => getPriceSettings());

  const { t } = useTranslation();

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key && event.key !== PRICE_SETTINGS_STORAGE_KEY) return;
      setPriceSettings(getPriceSettings());
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Initialize menu selection from active selection when menu opens or active selection changes
  // useEffect removed to fix flash issue logic moved to button click

  useEffect(() => {
    // If on watchlist section but user is not logged in, redirect to default
    // We only want to enforce this if it's explicitly the active section but we know there's no user
    if (activeSectionId === 'watchlist' && currentUser === null) {
      // Actually we'll assume Firebase takes a moment. So let's allow it to render a placeholder
      // or we can just redirect if they click it.
      // Wait, we handle clicking it. What if it's stored in local storage?
      // Let's just leave it and display empty or login message on the page if they load directly into it
    }
  }, [activeSectionId, currentUser]);

  useEffect(() => {
    localStorage.setItem('activeGearSection', activeSectionId);
  }, [activeSectionId]);

  useEffect(() => {
    if (initializedDefaultSectionRef.current) return;
    if (!Array.isArray(currentUser?.gearWatchlist)) return;

    initializedDefaultSectionRef.current = true;

    const savedSection = getStoredSelection('activeGearSection');
    if (!savedSection && currentUser.gearWatchlist.length > 0) {
      setActiveSectionId('watchlist');
      setActiveCategoryId('watchlist-items');
      setActiveSubcategoryId('watchlist-items-all');
      setActiveItemId('watchlist-items-all-entries');
    }
  }, [currentUser?.gearWatchlist]);

  useEffect(() => {
    if (activeCategoryId) {
      localStorage.setItem('activeGearCategory', activeCategoryId);
    }
  }, [activeCategoryId]);

  useEffect(() => {
    if (activeSubcategoryId) {
      localStorage.setItem('activeGearSubcategory', activeSubcategoryId);
    }
  }, [activeSubcategoryId]);

  useEffect(() => {
    if (activeItemId) {
      localStorage.setItem('activeGearItem', activeItemId);
    }
  }, [activeItemId]);

  useEffect(() => {
    const section = MENU.find((s) => s.id === activeSectionId);
    if (section) {
      const firstCategory = section.categories[0];
      if (!activeCategoryId || !section.categories.find((c) => c.id === activeCategoryId)) {
        setActiveCategoryId(firstCategory.id);
      }
    }
  }, [activeSectionId, activeCategoryId]);

  useEffect(() => {
    const section = MENU.find((s) => s.id === activeSectionId);
    const category = section?.categories.find((c) => c.id === activeCategoryId);
    if (category) {
      const firstSubcategory = category.subcategories[0];
      if (!activeSubcategoryId || !category.subcategories.find((s) => s.id === activeSubcategoryId)) {
        setActiveSubcategoryId(firstSubcategory.id);
      }
    }
  }, [activeSectionId, activeCategoryId, activeSubcategoryId]);

  useEffect(() => {
    const section = MENU.find((s) => s.id === activeSectionId);
    const category = section?.categories.find((c) => c.id === activeCategoryId);
    const subcategory = category?.subcategories.find((s) => s.id === activeSubcategoryId);
    if (subcategory) {
      const firstItem = subcategory.items[0];
      if (!activeItemId || !subcategory.items.find((i) => i.id === activeItemId)) {
        setActiveItemId(firstItem.id);
      }
    }
  }, [activeSectionId, activeCategoryId, activeSubcategoryId, activeItemId]);

  useEffect(() => {
    async function fetchGear() {
      try {
        setLoading(true);
        const requestedCurrency = priceSettings.viewMode === 'exchange'
          ? priceSettings.exchangeTargetCurrency
          : 'EUR';
        const res = await fetch(`${API_BASE_URL}/api/gear?currency=${encodeURIComponent(requestedCurrency)}`);
        const data = await res.json();
        if (data.success) {
          setGear(data.data);
        } else {
          setError(data.message || t('gear.error'));
        }
      } catch (e) {
        setError(e.message || t('gear.error'));
      } finally {
        setLoading(false);
      }
    }
    fetchGear();
  }, [t, priceSettings.exchangeTargetCurrency, priceSettings.viewMode]);

  const activeSection = MENU.find((s) => s.id === activeSectionId) || MENU[0];
  const activeCategory = activeSection.categories.find((c) => c.id === activeCategoryId) || activeSection.categories[0];
  const activeSubcategory = activeCategory.subcategories.find((s) => s.id === activeSubcategoryId) || activeCategory.subcategories[0];
  const activeItem = activeSubcategory.items.find((i) => i.id === activeItemId) || activeSubcategory.items[0];

  const getFilteredItems = (itemConfig) => {
    let items = gear;
    const filters = itemConfig.filters || {};
    const dataCategories = itemConfig.dataCategories || [];

    const toNumber = (value) => {
      if (typeof value === 'number' && Number.isFinite(value)) return value;
      const normalized = String(value || '').replace(',', '.').replace(/[^0-9.-]/g, '').trim();
      if (!normalized) return null;
      const parsed = Number.parseFloat(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const parseCurrencyString = (val) => {
      const str = String(val || '');
      const num = toNumber(str);
      const symbol = str.replace(/[\d.,-\s]/g, '').trim().toUpperCase();
      return { num, symbol };
    };

    const getDiscountPercent = (item) => {
      const values = [];

      const topLevelDiscount = toNumber(item.saleDiscountPercent);
      if (Number.isFinite(topLevelDiscount) && topLevelDiscount > 0) values.push(topLevelDiscount);

      const oldPriceData = parseCurrencyString(item.oldPrice);
      const priceData = parseCurrencyString(item.price);
      if (Number.isFinite(oldPriceData.num) && Number.isFinite(priceData.num) && oldPriceData.num > 0 && priceData.num < oldPriceData.num) {
        // Enforce matching currency symbols (or one being empty) to prevent false discounts
        if (!oldPriceData.symbol || !priceData.symbol || oldPriceData.symbol === priceData.symbol) {
          values.push(((oldPriceData.num - priceData.num) / oldPriceData.num) * 100);
        }
      }

      if (Array.isArray(item.offers)) {
        item.offers.forEach((offer) => {
          const offerDiscount = toNumber(offer?.saleDiscountPercent);
          if (Number.isFinite(offerDiscount) && offerDiscount > 0) {
            values.push(offerDiscount);
            return;
          }

          const offerOld = parseCurrencyString(offer?.oldPrice);
          const offerNew = parseCurrencyString(offer?.price);
          
          if (Number.isFinite(offerOld.num) && Number.isFinite(offerNew.num) && offerOld.num > 0 && offerNew.num < offerOld.num) {
             // Verify offer currencies match
             const offerOldSymbol = offerOld.symbol || String(offer?.oldPriceCurrency || offer?.currency || '').toUpperCase();
             const offerNewSymbol = offerNew.symbol || String(offer?.currency || '').toUpperCase();
             
             if (!offerOldSymbol || !offerNewSymbol || offerOldSymbol === offerNewSymbol) {
               values.push(((offerOld.num - offerNew.num) / offerOld.num) * 100);
             }
          }
        });
      }

      if (!values.length) return 0;
      return Math.max(...values);
    };

    const isDiscounted = (item) => {
      if (item?.isOnSale === true) return true;
      if (Array.isArray(item?.offers) && item.offers.some((offer) => offer?.isOnSale === true)) return true;
      return getDiscountPercent(item) > 0;
    };

    if (itemConfig.id === 'men-sales-all-items-men' || itemConfig.id === 'women-sales-all-items-women') {
      items = items.filter(item => !SALES_EQUIPMENT_CATEGORIES.includes(item.category) && !SALES_EQUIPMENT_CATEGORIES.includes(item.subCategory));
    }

    const normalizeGender = (value) => {
      const normalized = String(value || '').trim().toLowerCase();
      if (normalized.includes('women') || normalized.includes('female') || normalized.includes('woman')) return 'women';
      if (normalized.includes('men') || normalized.includes('male') || normalized.includes('man')) return 'men';
      if (normalized.includes('unisex')) return 'unisex';
      return '';
    };

    // Sorting
    if (filters.sort === 'price-asc') {
        const getPrice = (g) => {
            if (typeof g.price === 'number') return g.price;
            const match = String(g.price).match(/[\d\.]+/);
            return match ? parseFloat(match[0]) : 0;
        };
        items = [...items].sort((a, b) => getPrice(a) - getPrice(b));
    }

    // Category Filtering
    if (dataCategories.length > 0) {
      items = items.filter((item) =>
        dataCategories.includes(item.category) || 
        dataCategories.includes(item.subCategory)
      );
    } else {
        const hasFilters = 
         filters.cramponCompat ||
         filters.cramponCat ||
         filters.litersMin ||
         filters.litersMax ||
         filters.litersMissing ||
         filters.subCategory ||
         filters.textIncludes ||
         filters.sort ||
         filters.genderIncludes ||
         filters.saleOnly ||
         Number.isFinite(filters.minDiscountPercent);
       
       if (!hasFilters) return [];
    }

    // Subcategory Filter (Legacy)
    if (filters.subCategory) {
      items = items.filter((item) => String(item.subCategory || '') === filters.subCategory);
    }

    const appliesGenderSectionFilter =
      (activeSectionId === 'men' || activeSectionId === 'women')
      && dataCategories.length > 0
      && dataCategories.some((categoryName) => CLOTHING_CATEGORIES.has(categoryName) || FOOTWEAR_CATEGORIES.has(categoryName));

    if (appliesGenderSectionFilter) {
      const targetGender = activeSectionId === 'women' ? 'women' : 'men';
      items = items.filter((item) => normalizeGender(item.gender) === targetGender);
    }

    // Text Search
    if (filters.textIncludes) {
      const tokens = filters.textIncludes.map((token) => token.toLowerCase());
      items = items.filter((item) => {
        const haystack = `${item.name || ''} ${item.description || ''} ${item.brand || ''} ${item.category || ''} ${item.subCategory || ''}`.toLowerCase();
        return tokens.some((token) => haystack.includes(token));
      });
    }

    if (Array.isArray(filters.genderIncludes) && filters.genderIncludes.length > 0) {
      const genders = filters.genderIncludes.map((value) => String(value).toLowerCase());
      items = items.filter((item) => {
        const value = String(item.gender || '').toLowerCase();
        if (!value) return true;
        return genders.some((genderToken) => value.includes(genderToken));
      });
    }

    if (filters.saleOnly || Number.isFinite(filters.minDiscountPercent)) {
      const minDiscountPercent = Number.isFinite(filters.minDiscountPercent) ? filters.minDiscountPercent : 0;
      items = items.filter((item) => isDiscounted(item) && getDiscountPercent(item) > minDiscountPercent);
    }

    if (filters.watchlistOnly) {
      const watchlistSet = new Set((currentUser?.gearWatchlist || []).map((id) => String(id || '').trim()));
      items = items.filter((item) => watchlistSet.has(String(item?.id || '').trim()));
    }

    // Boot Category (Maps to boot_category for B1..B3)
    if (filters.cramponCompat) {
      items = items.filter((item) => {
          const val = item.boot_category || item.cramponCompat || '';
          return filters.cramponCompat.includes(String(val).toUpperCase());
      });
    }

    // Crampon Binding (Maps to binding_type for C1..C3)
    if (filters.cramponCat) {
      items = items.filter((item) => {
         const val = item.binding_type || item.cramponCat || [];
         const valArray = Array.isArray(val) ? val : [val];
         return filters.cramponCat.some(f => valArray.includes(f));
      });
    }

    if (filters.isHybridCrampon) {
      items = items.filter((item) => {
         const val = item.binding_type || item.cramponCat || [];
         const valArray = Array.isArray(val) ? val : [val];
         return valArray.length > 1;
      });
    }

    // Capacity (Maps to capacity for Backpacks)
    if (filters.litersMissing) {
      items = items.filter((item) => !Number.isFinite(parseFloat(item.capacity || item.liters)));
    }

    if (filters.litersMin || filters.litersMax) {
      items = items.filter((item) => {
        const value = parseFloat(item.capacity || item.liters);
        if (!Number.isFinite(value)) return false;
        if (Number.isFinite(filters.litersMin) && value < filters.litersMin) return false;
        if (Number.isFinite(filters.litersMax) && value > filters.litersMax) return false;
        return true;
      });
    }
    
    // Limit
    if (filters.limit) {
        items = items.slice(0, filters.limit);
    }

    return items;
  };

  const handleMenuSelection = (type, id) => {
    if (type === 'section') {
      if (id === 'watchlist' && !currentUser) {
        if (onOpenLogin) onOpenLogin();
        setIsMenuOpen(false);
        return;
      }
      
      const section = MENU.find((s) => s.id === id);
      if (section && section.categories.length === 1) {
        const category = section.categories[0];
        if (category.subcategories && category.subcategories.length === 1) {
          const subcategory = category.subcategories[0];
          const hasItems = Array.isArray(subcategory.items) && subcategory.items.length > 0;
          if (hasItems && !category.comingSoon && !subcategory.comingSoon) {
            setMenuSelection({ section: id, category: category.id, subCategory: subcategory.id });
            setActiveSectionId(id);
            setActiveCategoryId(category.id);
            setActiveSubcategoryId(subcategory.id);
            setIsMenuOpen(false);
            return;
          }
        }
      }
      setMenuSelection({ section: id, category: null, subCategory: null });
      return;
    }

    if (type === 'category') {
      // Prevent selecting categories that contain no subcategories/items
      const section = MENU.find(s => s.id === menuSelection.section);
      if (!section) return;
      const category = section.categories.find(c => c.id === id);
      const hasItems = category && Array.isArray(category.subcategories) && category.subcategories.some(sc => Array.isArray(sc.items) && sc.items.length > 0);
      if (!hasItems || category.comingSoon) return;

      if (category.subcategories && category.subcategories.length === 1) {
        const subcategory = category.subcategories[0];
        const subHasItems = Array.isArray(subcategory.items) && subcategory.items.length > 0;
        if (subHasItems && !subcategory.comingSoon) {
          setMenuSelection(prev => ({ ...prev, category: id, subCategory: subcategory.id }));
          setActiveSectionId(menuSelection.section);
          setActiveCategoryId(id);
          setActiveSubcategoryId(subcategory.id);
          setIsMenuOpen(false);
          return;
        }
      }

      setMenuSelection(prev => ({ ...prev, category: id, subCategory: null }));
      return;
    }

    if (type === 'subCategory') {
      // Prevent selecting empty subcategories
      const section = MENU.find(s => s.id === menuSelection.section);
      const category = section?.categories.find(c => c.id === menuSelection.category);
      const subcategory = category?.subcategories.find(sc => sc.id === id);
      const hasItems = subcategory && Array.isArray(subcategory.items) && subcategory.items.length > 0;
      if (!hasItems || subcategory.comingSoon) return;

      setMenuSelection(prev => ({ ...prev, subCategory: id }));

      // Close menu and select subcategory when subcategory is clicked
      setActiveSectionId(menuSelection.section);
      setActiveCategoryId(menuSelection.category);
      setActiveSubcategoryId(id);
      setIsMenuOpen(false);
      return;
    }
  };

  const renderDropdownMenu = () => {
    if (!isMenuOpen) return null;

    const sections = MENU;
    const selectedSection = sections.find(s => s.id === menuSelection.section);
    const categories = selectedSection ? selectedSection.categories : [];
    
    const selectedCategory = categories.find(c => c.id === menuSelection.category);
    const subcategories = selectedCategory ? selectedCategory.subcategories : [];
    
    const selectedSubcategory = subcategories.find(s => s.id === menuSelection.subCategory);
    const items = selectedSubcategory ? selectedSubcategory.items : [];

    const ChevronRight = () => (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    );

    const BackArrow = () => (
       <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px' }}>
         <line x1="19" y1="12" x2="5" y2="12"></line>
         <polyline points="12 19 5 12 12 5"></polyline>
       </svg>
    );

    const handleBack = (level) => {
      // level: 'section' | 'category' | 'subCategory' (what we are clearing)
      if (level === 'section') {
         setMenuSelection(prev => ({ ...prev, section: null, category: null, subCategory: null }));
      } else if (level === 'category') {
         setMenuSelection(prev => ({ ...prev, category: null, subCategory: null }));
      } else if (level === 'subCategory') {
         setMenuSelection(prev => ({ ...prev, subCategory: null }));
      }
    };

    return (
      <>
        <div className={styles.overlay} onClick={() => setIsMenuOpen(false)} />
        <div className={styles.dropdownMenu} data-has-section={!!menuSelection.section} data-has-category={!!menuSelection.category}>
          {/* Level 1: Sections */}
          <div className={`${styles.menuColumn} ${styles.sectionColumn}`}>
            <div className={styles.menuHeader}>{t('gear.title')}</div>
            {sections.map(section => (
              <button
                key={section.id}
                className={`${styles.menuItem} ${menuSelection.section === section.id ? styles.menuItemActive : ''}`}
                onClick={() => handleMenuSelection('section', section.id)}
              >
                {section.label}
                <ChevronRight />
              </button>
            ))}
          </div>

          {/* Level 2: Categories */}
          {categories.length > 0 && (
            <div className={`${styles.menuColumn} ${styles.categoryColumn}`}>
              <div className={styles.mobileBackRow} onClick={() => handleBack('section')}>
                <BackArrow /> {t('gear.back')}
              </div>
              <div className={styles.menuHeader}>{selectedSection.label}</div>
              {categories.map(category => (
                    (() => {
                      const hasItems = Array.isArray(category.subcategories) && category.subcategories.some(sc => Array.isArray(sc.items) && sc.items.length > 0);
                      const isComingSoon = category.comingSoon;
                      const isDisabled = !hasItems || isComingSoon;
                      const classes = `${styles.menuItem} ${menuSelection.category === category.id ? styles.menuItemActive : ''} ${isDisabled ? styles.menuItemDisabled : ''}`;
                      return (
                        <button
                          key={category.id}
                          className={classes}
                          onClick={() => !isDisabled && handleMenuSelection('category', category.id)}
                          disabled={isDisabled}
                          style={isComingSoon ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {category.label}
                            {isComingSoon && <span style={{ fontSize: '0.7em', padding: '2px 6px', background: 'rgba(128,128,128,0.2)', borderRadius: '12px', whiteSpace: 'nowrap' }}>Coming soon...</span>}
                          </span>
                          <ChevronRight />
                        </button>
                      );
                    })()
              ))}
            </div>
          )}

          {/* Level 3: Subcategories (Final Selection) */}
          {subcategories.length > 0 && (
            <div className={`${styles.menuColumn} ${styles.subCategoryColumn}`}>
              <div className={styles.mobileBackRow} onClick={() => handleBack('category')}>
                <BackArrow /> {t('gear.back')}
              </div>
              <div className={styles.menuHeader}>{selectedCategory.label}</div>
              {subcategories.map(subcategory => (
                (() => {
                  const hasItems = Array.isArray(subcategory.items) && subcategory.items.length > 0;
                  const isComingSoon = subcategory.comingSoon;
                  const isDisabled = !hasItems || isComingSoon;
                  const classes = `${styles.menuItem} ${menuSelection.subCategory === subcategory.id ? styles.menuItemActive : ''} ${isDisabled ? styles.menuItemDisabled : ''}`;
                  return (
                    <button
                      key={subcategory.id}
                      className={classes}
                      onClick={() => !isDisabled && handleMenuSelection('subCategory', subcategory.id)}
                      disabled={isDisabled}
                      style={isComingSoon ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    >
                      {subcategory.id === 'watchlist-items-all' 
                        ? `Watchlist (${Array.isArray(currentUser?.gearWatchlist) ? Math.min(currentUser.gearWatchlist.length, 10) : 0}/10)` 
                        : subcategory.label}
                      {isComingSoon && <span style={{ fontSize: '0.7em', padding: '2px 6px', background: 'rgba(128,128,128,0.2)', borderRadius: '12px', whiteSpace: 'nowrap', marginLeft: 'auto' }}>Coming soon...</span>}
                    </button>
                  );
                })()
              ))}
            </div>
          )}
        </div>
      </>
    );
  };

  if (SHOW_GEAR_PAGE_PLACEHOLDER) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        padding: '2rem'
      }}>
        <Helmet>
          <title>Something Big is Coming · Gear</title>
        </Helmet>
        <h1 style={{
          fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
          lineHeight: '1.4',
          margin: '0',
          whiteSpace: 'pre-line',
          color: 'var(--text-color)'
        }}>
          Something big is coming... {"\n"} Check back later!
        </h1>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('gear.helmetTitle')}</title>
        <meta name="description" content={t('gear.helmetDescription')} />
      </Helmet>

      <div className={styles.pageContainer}>
        <div className={styles.headerRow}>
          <button
            className={styles.menuButton}
            onClick={() => {
              const newState = !isMenuOpen;
              setIsMenuOpen(newState);
              if (newState) {
                setMenuSelection({
                  section: null,
                  category: null,
                  subCategory: null
                });
              }
            }}
            aria-label="Toggle menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          </button>
          <h2 className={styles.pageTitle}>
            {t('gear.title')}
            <span 
              onClick={() => setShowBetaPopup(true)}
              style={{ 
                fontSize: '0.8rem', 
                verticalAlign: 'top', 
                marginLeft: '0.5rem',
                backgroundColor: 'var(--color3)',
                color: 'var(--bg-color)',
                padding: '0.2rem 0.5rem',
                borderRadius: '999px',
                fontWeight: '700',
                textTransform: 'uppercase',
                cursor: 'pointer'
              }}
              title="Click to read Beta features info"
            >
              Beta
            </span>
          </h2>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {(() => {
              const sId = activeSubcategory.id;
              let guideUrl = '#'; // Placeholder
              let guideText = t('gear.howToBuy', { category: activeSubcategory.label.toLowerCase() }) + ' (Coming soon)';
              
              if (sId.includes('sales') || sId.includes('watchlist')) return null;
              
              if (sId.includes('mountaineering') || sId.includes('hiking') || sId.includes('trail')) {
                guideUrl = '/gear/guide/boots';
                guideText = t('gear.howToBuy', { category: activeSubcategory.label.toLowerCase() });
              } else if (sId.includes('hardshell')) {
                guideUrl = '/gear/guide/hardshell';
                guideText = t('gear.howToBuy', { category: 'hardshells' });
              } else if (sId.includes('softshell')) {
                guideUrl = '/gear/guide/softshell';
                guideText = t('gear.howToBuy', { category: 'softshells' });
              } else if (sId.includes('midlayer') || sId.includes('insulation')) {
                guideUrl = '/gear/guide/midlayer';
                guideText = t('gear.howToBuy', { category: 'midlayers & insulation' });
              } else if (sId.includes('baselayer')) {
                guideUrl = '/gear/guide/base-layer';
                guideText = t('gear.howToBuy', { category: 'base layers' });
              } else if (sId.includes('backpacks')) {
                guideUrl = '/gear/guide/backpacks';
                guideText = t('gear.howToBuy', { category: 'backpacks' });
              } else if (sId.includes('ice-axes')) {
                guideUrl = '/gear/guide/ice-axes';
                guideText = t('gear.howToBuy', { category: 'ice axes' });
              } else if (sId.includes('crampons')) {
                guideUrl = '/gear/guide/crampons';
                guideText = t('gear.howToBuy', { category: 'crampons' });
              } else if (sId.includes('accessories') || sId.includes('eyewear')) {
                guideUrl = '/gear/guide/other';
                guideText = t('gear.howToBuy', { category: 'accessories' });
              } else if (sId.includes('trekking-poles')) {
                guideUrl = '/gear/guide/trekking-poles';
                guideText = t('gear.howToBuy', { category: 'trekking poles' });
              } else if (sId.includes('headlamps')) {
                guideUrl = '/gear/guide/headlamps';
                guideText = t('gear.howToBuy', { category: 'headlamps' });
              } else if (sId.includes('location')) {
                guideUrl = '/gear/guide/location';
                guideText = t('gear.howToBuy', { category: 'location gear' });
              } else if (sId.includes('climbing')) {
                guideUrl = '/gear/guide/climbing';
                guideText = t('gear.howToBuy', { category: 'climbing gear' });
              }

              return (
                <Link to={guideUrl} className={styles.buyingGuideLink}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <path d="M12 16v-4"></path>
                    <path d="M12 8h.01"></path>
                  </svg>
                  {guideText}
                </Link>
              );
            })()}
            <Link to="/gear/compare" className={styles.compareLink} style={{ marginLeft: 0 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 3h5v5"></path>
                <path d="M8 3H3v5"></path>
                <path d="M12 22v-8.3a4 4 0 0 0-1.172-2.872L3 3"></path>
                <path d="m15.172 9.128L21 3"></path>
              </svg>
              Compare
            </Link>
          </div>

          {renderDropdownMenu()}
        </div>

        <button
          type="button"
          className={styles.weightCalcButton}
          onClick={() => setShowCalculator(true)}
        >
          {t('gear.packWeight')}
        </button>

        <WeightCalculatorPopup
          isOpen={showCalculator}
          onClose={() => setShowCalculator(false)}
          gearItems={gear}
        />

        <BetaGearPopup isOpen={showBetaPopup} onClose={() => setShowBetaPopup(false)} />

        {loading ? (
          <GearPageSkeleton />
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : (
          <div className={styles.categoryContainer} style={{ padding: '0 2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '1.8rem', color: 'var(--text-color)', margin: 0 }}>
                {activeSubcategory.id === 'watchlist-items-all' 
                  ? `Watchlist (${Array.isArray(currentUser?.gearWatchlist) ? Math.min(currentUser.gearWatchlist.length, 10) : 0}/10)` 
                  : activeSubcategory.label}
              </h2>
              {(() => null)()}
            </div>

            {activeSubcategory.items.map((itemConfig) => {
              // Only render watchlist if user is logged in
              if (itemConfig.filters?.watchlistOnly && !currentUser) {
                 return (
                   <div key={itemConfig.id} style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-color)' }}>
                     {t('gear.loginToViewWatchlist', { defaultValue: 'Please log in to view your watchlist.' })}
                   </div>
                 );
              }
              const filteredItems = getFilteredItems(itemConfig);
              if (filteredItems.length === 0) return null;

              return (
                <GearCategorySection
                  key={itemConfig.id}
                  title={itemConfig.label}
                  items={filteredItems}
                  categoryType={itemConfig.viewId || 'default'}
                />
              );
            })}

            {activeSubcategory.items.every((item) => {
              if (item.filters?.watchlistOnly && !currentUser) return false;
              return getFilteredItems(item).length === 0;
            }) && (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>{t('gear.noGear')}</div>
            )}
          </div>
        )}
      </div>
    </>

  );
}

export default GearPage;
